import { compile } from "path-to-regexp";
import qs from "qs";

import { assertPathParamsMatchTemplate } from "./assertPathParamsMatchTemplate";

// `route()` declares a single URL: a callable URL builder with the path
// template, human title, description, and param metadata attached as readable
// props.
//
// Two consumers:
//   - Frontend callsites: `memberTagRoutes.edit({ path: { id } })` builds the URL.
//   - Codegen + tooling: `memberTagRoutes.edit.path` etc. reads the metadata.
//
// URL construction delegates to `path-to-regexp`'s `compile` (segment-aware
// substitution, encodeURIComponent of values, runtime errors on missing
// params) and `qs.stringify` for query strings. `qs` natively supports
// nested objects (`filter[live_at][gte]=...`) and arrays
// (`filters[0][key]=...`), so call-sites can pass them through verbatim.
//
// Safety property: pathParams keys must exactly match :param placeholders
// in the template (extracted via path-to-regexp). Mismatches throw at module
// load — caught in CI.

// Scalar types appear at any depth inside a query schema. `object` carries
// a recursive `properties` map (e.g. `filter: object → live_at: object →
// gte: number`). `array` carries a single `items` shape for the element
// type (e.g. `filters: array of object`). Path params stay scalar only —
// URL segments don't nest.
export type ScalarParamType = "number" | "string" | "boolean";
export type ParamType = ScalarParamType | "object" | "array";

export interface ScalarParamDef {
  description: string;
  type: ScalarParamType;
}

export interface ObjectParamDef {
  description: string;
  properties: Record<string, ParamDef>;
  type: "object";
}

export interface ArrayParamDef {
  description: string;
  items: ParamDef;
  type: "array";
}

export type ParamDef = ScalarParamDef | ObjectParamDef | ArrayParamDef;

export type PathParamDef = ScalarParamDef;
export type RouteAudience = "admin" | "moderator" | "member" | "public";

export interface RouteOptions {
  description: string;
  pathParams?: Record<string, PathParamDef>;
  queryParams?: Record<string, ParamDef>;
  requires: RouteAudience;
  title: string;
}

export type PathParamValues = Record<string, string | number>;
export type QueryParamValues = Record<string, unknown>;

export interface RouteBuildArgs {
  path?: PathParamValues;
  query?: QueryParamValues;
}

export interface RouteFn<TPath extends string = string> {
  (args?: RouteBuildArgs): string;
  description: string;
  path: TPath;
  pathParams?: Record<string, PathParamDef>;
  queryParams?: Record<string, ParamDef>;
  requires: RouteAudience;
  title: string;
}

function appendQueryString(path: string, query: QueryParamValues): string {
  const compact = Object.fromEntries(
    Object.entries(query).filter(
      ([, value]) => value !== undefined && value !== "",
    ),
  );
  return path + qs.stringify(compact, { addQueryPrefix: true });
}

function stringifyPathParams(params: PathParamValues): Record<string, string> {
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, String(value)]),
  );
}

export function route<TPath extends string>(
  template: TPath,
  options: RouteOptions,
): RouteFn<TPath> {
  assertPathParamsMatchTemplate(template, options.pathParams);

  const toPath = compile(template);

  const fn = ((args?: RouteBuildArgs) => {
    const path = toPath(stringifyPathParams(args?.path ?? {}));
    return args?.query ? appendQueryString(path, args.query) : path;
  }) as RouteFn<TPath>;

  Object.defineProperty(fn, "path", { value: template, enumerable: true });
  Object.defineProperty(fn, "description", {
    value: options.description,
    enumerable: true,
  });
  Object.defineProperty(fn, "title", {
    value: options.title,
    enumerable: true,
  });
  Object.defineProperty(fn, "requires", {
    value: options.requires,
    enumerable: true,
  });
  if (options.pathParams) {
    Object.defineProperty(fn, "pathParams", {
      value: options.pathParams,
      enumerable: true,
    });
  }
  if (options.queryParams) {
    Object.defineProperty(fn, "queryParams", {
      value: options.queryParams,
      enumerable: true,
    });
  }

  return fn;
}
